import { Dictionary, groupBy } from 'lodash'
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string'
import uuid from 'uuid/v4'
import { canHaveValueOperand, Operator } from '@global/constants'
import { ColumnDataType } from '@global/types'
import { rowPropertyAliases } from '../../datasets/handsontable/read-grid/readGridToolbar/components/toggleColumns/config'
import {
  DbColumnName,
  RowPropertyName,
} from '../../datasets/handsontable/read-grid/readGridToolbar/components/toggleColumns/types'
import { SubmissionPartial } from '../../datasets/hooks/useSubmissionPartials'

interface ValueObj {
  id: number | string
  name: string
}

interface DefaultFieldFilter {
  fieldId: string
  operator: Operator
  value: string | string[] | ValueObj | ValueObj[]
}

export const dbColumnToRowProperty = new Map<DbColumnName, RowPropertyName>([
  ['cv_id', RowPropertyName.cvId],
  ['properties.createdAt', RowPropertyName.createdAt],
  ['properties.createdById', RowPropertyName.createdBy],
  ['properties.submissionId', RowPropertyName.submission],
  ['properties.updatedAt', RowPropertyName.updatedAt],
  ['properties.updatedById', RowPropertyName.updatedBy],
])

export function generateSortParam({ fieldId, sort }): string | undefined {
  try {
    return compressToEncodedURIComponent(JSON.stringify([fieldId, sort]))
  } catch {
    console.warn(`Unable to encode sort parameter for field ${fieldId}`)
  }
}

export function generateSearchParam({
  fieldId,
  searchTerm,
  columnDataType,
  submissionIds,
}): string {
  try {
    return compressToEncodedURIComponent(
      JSON.stringify([fieldId, searchTerm, columnDataType, submissionIds]),
    )
  } catch {
    console.warn(`Unable to encode Search parameter for field ${fieldId}`)
  }
  return ''
}

export function generateFilterParam({ fieldId, operator, value }: DefaultFieldFilter): string {
  const rowPropertyName = dbColumnToRowProperty.get(fieldId as DbColumnName)
  if (
    rowPropertyName &&
    (isUserProperty(rowPropertyName) || isSubmissionProperty(rowPropertyName))
  ) {
    const convertedValue: unknown = value

    const filterValue =
      isSubmissionProperty(rowPropertyName) && Array.isArray(convertedValue)
        ? convertedValue?.map(v => v.name)
        : (convertedValue as ValueObj)

    return compressToEncodedURIComponent(JSON.stringify([rowPropertyName, operator, filterValue]))
  }

  return compressToEncodedURIComponent(JSON.stringify([fieldId, operator, value]))
}

export function extractSortOrdersFromParams({
  urlSearchParams,
}: {
  urlSearchParams: URLSearchParams
}): Record<
  string,
  | {
      fieldId: string
      sort: 'asc' | 'desc'
    }
  | object
> {
  const key = 'sort'
  const value = urlSearchParams.getAll(key)[0]
  if (!value) return {}

  urlSearchParams.set(key, value?.split(',')?.join('&'))

  const allParamsForKey = value?.split('&')
  const sortOrders = allParamsForKey.map(param => createSortOrderObj(param))

  return groupBy(sortOrders, 'fieldId')
}

function createSortOrderObj(param: string): { fieldId: string; sort: 'asc' | 'desc' } | object {
  if (!param) return {}

  try {
    const [fieldId, sort] = JSON.parse(decompressFromEncodedURIComponent(param))

    return {
      fieldId,
      sort,
    }
  } catch {
    console.warn(`Could not decode sort parameter ${param}`)
    return {}
  }
}

interface SearchByField {
  fieldId: number
  search: string
  columnDataType: ColumnDataType
  submissionIds: number[]
}

/**
 * Extract search param from URL and return grouped field by id
 * @param {Object.<string, string>} currentParams - UrlParam that contains search.
 * @param {string=} currentParams.search - search param.
 * @returns grouped field by id - `{[1]: {fieldId: 1, search: 'john'}}`
 */
export function extractSearchFromParams(currentParams: {
  search?: string
}): Dictionary<SearchByField[]> | null {
  const searchParams = currentParams?.search

  if (!searchParams) return null

  const searchs: SearchByField[] = Array.isArray(searchParams)
    ? (searchParams.map(createListOfSearchParams) as SearchByField[])
    : ([createListOfSearchParams(searchParams)] as SearchByField[])

  return groupBy<SearchByField>(searchs, 'fieldId')
}

function createListOfSearchParams(searchParam: string): SearchByField | Record<string, never> {
  try {
    const [fieldId, search, columnDataType, submissionIds] = JSON.parse(
      decompressFromEncodedURIComponent(searchParam),
    )
    return {
      fieldId,
      search,
      columnDataType,
      submissionIds,
    }
  } catch {
    console.warn('Unable to parse search filter json.')
    return {}
  }
}

export function extractFilterFromParams({
  urlSearchParams,
  key = 'filter',
  makeId = true,
  groupedByName = {},
}: {
  urlSearchParams: URLSearchParams
  key?: 'filter'
  makeId?: boolean
  groupedByName?: Record<string, SubmissionPartial[]>
}): DefaultFieldFilter | DefaultFieldFilter[] {
  if (urlSearchParams.getAll(key).length === 0) return []

  const allParamsForKey = urlSearchParams
    .getAll(key)?.[0]
    ?.split(',')
    .filter(v => !!v)

  if (!allParamsForKey?.length) return []

  const filters = allParamsForKey.map(param => createFilterObj(param, makeId, groupedByName))

  return filters.length > 1 ? filters : filters[0]!
}

// Extract the necessary information from URL parameters
export function getInfoFromParams(currentParams, groupedByName) {
  const urlParams = new URLSearchParams(currentParams)

  return {
    filters: extractFilterFromParams({
      urlSearchParams: urlParams,
      groupedByName,
    }),
    sortOrdersByFieldId: extractSortOrdersFromParams({ urlSearchParams: urlParams }),
  }
}

function createFilterObj(
  param: string,
  makeId: boolean,
  groupedByName: Record<string, SubmissionPartial[]>,
) {
  const [fieldId, operator, valueParts]: [string, Operator, any] = (() => {
    try {
      const parsed = JSON.parse(decompressFromEncodedURIComponent(param))

      if (!Array.isArray(parsed) && parsed.length !== 3) return ''

      return parsed
    } catch {
      console.warn('Unable to parse filter json.')
      return ''
    }
  })()

  const dbColumnNameForRowProperty = rowPropertyAliases.get(fieldId as RowPropertyName)?.db
  const value = (() => {
    if (!canHaveValueOperand(operator)) {
      return ''
    }

    const valueShouldBeArray = operator === Operator.IN || operator === Operator.NOT_IN

    if (isUserProperty(fieldId as RowPropertyName)) {
      if (valueShouldBeArray) {
        return valueParts.map(value => {
          const { id, name } = value
          return { id: +id, name }
        })
      }

      const { id, name } = valueParts
      return { id: +id, name }
    }

    if (isSubmissionProperty(fieldId as RowPropertyName)) {
      if (valueShouldBeArray) {
        return valueParts.map(value => {
          const submissions = groupedByName[value]
          const ids = submissions?.map(sub => Number(sub.submission_id)) ?? []

          return {
            id: ids.length > 1 ? ids.join(',') : ids[0],
            name: value,
          }
        })
      }

      const [id, name] = valueParts

      return { id: +id, name }
    }

    return valueParts
  })()

  return {
    ...(makeId && { id: uuid() }),
    fieldId: dbColumnNameForRowProperty || fieldId,
    operator,
    value,
  }
}

export function isEncoded(value) {
  try {
    return decodeURIComponent(value) !== value
  } catch {
    return false
  }
}

export function decodeURIComponentAndDelimiters(value: string): string {
  return decodeURIComponent(value)
    .replaceAll('%7E', '~')
    .replaceAll('%5F', '_')
    .replaceAll('%2B', '+')
}

function isUserProperty(rowPropertyName: RowPropertyName): boolean {
  return (
    rowPropertyName === RowPropertyName.createdBy || rowPropertyName === RowPropertyName.updatedBy
  )
}

function isSubmissionProperty(rowPropertyName: RowPropertyName): boolean {
  return rowPropertyName === RowPropertyName.submission
}
